import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PageClasses from 'Webapp/shared/app/components/page-classes';
import styled from '@emotion/styled';
import { BREAKPOINTS } from 'Style/breakpoints';
import { SPACING } from 'Style/spacing';
import { BODY_TYPES, UI_SUBHEADING_TYPES } from 'Style/typography';

// Components
import Input from 'Webapp/shared/app/components/base/input';
import Button from 'Webapp/shared/app/components/button';
import LoadingSpinner from 'ComponentLibrary/icons/loading-spinner';
import SignupDisclaimer from 'Webapp/shared/app/components/signup-disclaimer';

// Utils
import sentry from 'Utils/sentry';

import { FrictionlessFlowType } from 'Webapp/enums';
import withFrictionlessSignup from 'Webapp/shared/app/hocs/withFrictionlessSignup';
import withT from 'ComponentLibrary/hocs/withT';
import withReCaptcha from 'Webapp/shared/app/hocs/withReCaptcha';

import connector from 'Utils/connector';

import connectEmailSettings from 'Webapp/shared/app/connectors/connectEmailSettings';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';

const Content = styled.div(
  {
    display: 'grid',
    columnGap: `${SPACING.BASE8X}`,
    gridTemplateColumns: 'minmax(200px, 500px) minmax(200px, 500px)',
    gridTemplateRows: '700px',
  },
  BREAKPOINTS.phone({
    display: 'block',
  }),
);

const StyledPageClasses = styled(PageClasses)({
  display: 'flex',
  alignItems: 'center',
  marginTop: SPACING.BASE6X,
});

const FormWrapper = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    h1: {
      ...BODY_TYPES.BRAND_HEADING_MEDIUM,
      paddingBottom: SPACING.BASE4X,
    },
    '.sub-heading': {
      ...UI_SUBHEADING_TYPES.LARGE_STANDARD,
      paddingBottom: SPACING.BASE6X,
    },
  },
  BREAKPOINTS.phone({
    marginTop: `${SPACING.BASE4X}`,
    marginBottom: `${SPACING.BASE4X}`,
  }),
);

const ImageWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  img: {
    objectFit: 'cover',
    height: '100%',
  },
});

const Form = styled.form({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  rowGap: `${SPACING.LARGE}`,
  marginBottom: `${SPACING.BASE}`,
});

const EmailInput = styled(Input)({
  width: '100%',
});

const SubmitButton = styled(Button)({
  width: 'fit-content',
});

const MAP_TO_SUBSCRIPTION_KEY = {
  '10fortoday': '104T',
  tech: 'Tech',
  photosoftheweek: 'PhotoDesk',
  politics: 'Polit',
  climate: 'Climate',
  theshot: 'Photography',
  recipes: 'Recipes',
  thetastiest: 'Food',
  savvy: 'PerFin',
  wanderlust: 'Travel',
};
class NewsLetterLanding extends Component {
  constructor(props) {
    super(props);

    this.submit = this.submit.bind(this);

    this.emailSubscriptions = props.pendingSubscriptionGroups.find(
      (s) => s.name.toLowerCase() === 'newsletters',
    );
    this.subscriptionKey = MAP_TO_SUBSCRIPTION_KEY[props.keyProperty];
    this.subscriptionGroupId = `${this.emailSubscriptions.group}-${this.subscriptionKey}`;
    this.subscription = this.emailSubscriptions.subscriptions.find(
      (s) => s.key === this.subscriptionKey,
    );
  }

  componentDidMount() {
    const { usageTrackEnterNewsletterLandingPage } = this.props;
    usageTrackEnterNewsletterLandingPage(this.subscriptionGroupId);
  }

  componentDidUpdate(prevProps) {
    const {
      reCaptchaFailedValidation,
      usageTrackNewsletterLandingPageSubmission,
    } = this.props;
    if (!prevProps.reCaptchaFailedValidation && reCaptchaFailedValidation) {
      usageTrackNewsletterLandingPageSubmission(
        this.subscriptionGroupId,
        false,
      );
    }
  }

  async submit(e) {
    e.preventDefault();
    const { validateReCaptcha, usageTrackNewsletterLandingPageSubmitClick } =
      this.props;
    usageTrackNewsletterLandingPageSubmitClick(this.subscriptionGroupId);
    await validateReCaptcha('newsletters_landing', async () => {
      const { onSubmit, usageTrackNewsletterLandingPageSubmission } =
        this.props;
      try {
        await onSubmit(e, FrictionlessFlowType.NEWSLETTER);
        usageTrackNewsletterLandingPageSubmission(
          this.subscriptionGroupId,
          true,
        );
      } catch (e) {
        usageTrackNewsletterLandingPageSubmission(
          this.subscriptionGroupId,
          false,
        );
        sentry.captureException(e);
      }
    });
  }

  render() {
    const {
      t,
      email,
      onboardPending,
      isReCaptchaPending,
      onEmailChange,
      configuration,
      isSubmittable,
      ReCaptchaUI,
      frictionlessEnter,
    } = this.props;
    const newsLetterHeaderTitle = t(
      `email_preference_subscription_name_${this.subscriptionKey}`,
    );

    const submitDisabled = isReCaptchaPending || !isSubmittable;

    return (
      <StyledPageClasses>
        <Content>
          <FormWrapper>
            <h1>
              {t('sign_up_email_newsletter_name', {
                newsLetterTheme: newsLetterHeaderTitle,
              })}
            </h1>
            <p className="sub-heading">{this.subscription?.description}</p>
            <Form onSubmit={this.submit}>
              <EmailInput
                type="email"
                name="email"
                placeholder={t('newsletters_email_placeholder')}
                value={email}
                onChange={onEmailChange}
                onKeyUp={() =>
                  frictionlessEnter(FrictionlessFlowType.NEWSLETTER)
                }
                disabled={onboardPending}
                autoFocus
              />
              {onboardPending ? (
                <LoadingSpinner />
              ) : (
                <SubmitButton
                  name="newsletters__form-submit"
                  className="button--primary button--block"
                  type="submit"
                  disabled={submitDisabled}
                >
                  Join
                </SubmitButton>
              )}
            </Form>
            {ReCaptchaUI}
            <SignupDisclaimer isNewsletter />
          </FormWrapper>
          <ImageWrapper>
            <img src={configuration.image} alt="imag-cover" />
          </ImageWrapper>
        </Content>
      </StyledPageClasses>
    );
  }
}

NewsLetterLanding.propTypes = {
  keyProperty: PropTypes.string.isRequired,
  configuration: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  emailGroups: PropTypes.array,
  emailGroupsLoaded: PropTypes.bool.isRequired,
  isPhone: PropTypes.bool.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onPendingSubscriptionGroupsChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pendingSubscriptionGroups: PropTypes.array,
  email: PropTypes.string.isRequired,
  onboardPending: PropTypes.bool.isRequired,
  isSubmittable: PropTypes.bool.isRequired,
  subscriptionGroups: PropTypes.array.isRequired,
  usageTrackSignupEnter: PropTypes.func.isRequired,
  usageTrackSignupExit: PropTypes.func.isRequired,
  usageTrackEnterNewsletterLandingPage: PropTypes.func.isRequired,
  usageTrackNewsletterLandingPageSubmitClick: PropTypes.func.isRequired,
  usageTrackNewsletterLandingPageSubmission: PropTypes.func.isRequired,
  preSelectSubscription: PropTypes.func.isRequired,
  ReCaptchaUI: PropTypes.node,
  validateReCaptcha: PropTypes.func.isRequired,
  isReCaptchaPending: PropTypes.bool.isRequired,
  reCaptchaFailedValidation: PropTypes.bool.isRequired,
  frictionlessEnter: PropTypes.func.isRequired,
};

export default connector(
  connectResponsive,
  connectEmailSettings,
)(withFrictionlessSignup(withReCaptcha(withT(NewsLetterLanding))));
